import { Button, cn, Select } from '@resolute/ui';
import { LayoutGrid, Map as MapIcon, SlidersHorizontal } from 'lucide-react';


import { SORT_OPTIONS } from '../constants';
import { type SearchState } from '../search-query';
import { type UseSearchState } from '../use-search-state';

export type ResultsView = 'list' | 'map';

interface ResultsToolbarProps extends Pick<UseSearchState, 'setSort'> {
  state: SearchState;
  total?: number;
  isLoading: boolean;
  activeFilterCount: number;
  onOpenFilters: () => void;
  view: ResultsView;
  onViewChange: (view: ResultsView) => void;
}

export function ResultsToolbar({
  state,
  total,
  isLoading,
  activeFilterCount,
  setSort,
  onOpenFilters,
  view,
  onViewChange,
}: ResultsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {isLoading || total == null ? (
          'Searching…'
        ) : (
          <>
            <span className="font-semibold text-foreground">{total.toLocaleString('en-IN')}</span>{' '}
            {total === 1 ? 'property' : 'properties'} found
          </>
        )}
      </p>

      {/* `flex-wrap` + `min-w-0`: the row holds a view toggle, a Filters button
          and a fixed-width sort select. Below ~360px those exceed the viewport
          and, without a wrap or a shrinkable select, pushed the whole page
          sideways. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* List / Map view toggle */}
        <div
          role="group"
          aria-label="Result view"
          className="inline-flex rounded-md border border-border p-0.5"
        >
          {(
            [
              { id: 'list', label: 'List', Icon: LayoutGrid },
              { id: 'map', label: 'Map', Icon: MapIcon },
            ] as const
          ).map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => onViewChange(id)}
              aria-pressed={view === id}
              className={cn(
                'inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm font-medium transition-colors duration-base ease-out-expo',
                view === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              <Icon className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="lg:hidden"
          onClick={onOpenFilters}
          aria-label="Open filters"
        >
          <SlidersHorizontal className="size-4" aria-hidden="true" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </Button>

        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground sm:flex-none">
          <span className="hidden sm:inline">Sort</span>
          {/* Fills the leftover width on a phone rather than insisting on 11rem
              and overflowing; back to its fixed width from `sm` up. */}
          <Select
            aria-label="Sort results"
            options={SORT_OPTIONS}
            value={state.sort}
            onChange={(e) => setSort(e.target.value as SearchState['sort'])}
            className="w-full min-w-0 sm:w-44"
          />
        </div>
      </div>
    </div>
  );
}
